import { useCallback } from "react";

import { getConfig } from '../config';
import { fetchWithAuth } from "../api/fetchWithAuth";
import { parseBlockSection } from "../layers/glo/parseBlockSection";

/* ------------------------------------------------------------------ */
/* PARSE LAT/LNG FROM A STRING                                        */
/* ------------------------------------------------------------------ */
// Accepts:  "31.9686, -102.0779"  |  "31.9686 -102.0779"  |  "-102.0779,31.9686"
// Heuristic: if the first number is in [-90, 90] AND the second is in [-180, 180],
// treat as "lat, lng" (the common human-typed form). Otherwise try the swap.
function tryParseLatLng(str) {
  if (!str) return null;
  const m = str.trim().match(/^(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  const a = parseFloat(m[1]);
  const b = parseFloat(m[2]);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;

  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return { lat: a, lng: b };
  if (Math.abs(b) <= 90 && Math.abs(a) <= 180) return { lat: b, lng: a };
  return null;
}

/* ------------------------------------------------------------------ */
/* MAPBOX GEOCODER                                                    */
/* ------------------------------------------------------------------ */
// Returns { lat, lng, name, bbox? } | null
async function geocodeMapbox(query, { signal, proximity } = {}) {
  const mapboxToken = getConfig().mapboxToken;
  if (!mapboxToken) {
    console.warn("[useFlyTo] mapboxToken missing — geocoding disabled");
    return null;
  }
  const params = new URLSearchParams({
    access_token: mapboxToken,
    limit: "1",
    // Bias to US — most of your data is Permian/Texas. Remove if you go global.
    country: "us",
  });
  if (proximity) params.set("proximity", `${proximity.lng},${proximity.lat}`);

  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
    query
  )}.json?${params.toString()}`;

  const res = await fetch(url, { signal });
  if (!res.ok) return null;
  const data = await res.json();
  const f = data?.features?.[0];
  if (!f) return null;

  const [lng, lat] = f.center;
  return {
    lat,
    lng,
    name: f.place_name,
    bbox: f.bbox, // [minLng, minLat, maxLng, maxLat] when available
  };
}

/* ------------------------------------------------------------------ */
/* GLO SURVEY BLOCK + SECTION                                         */
/* ------------------------------------------------------------------ */
// Returns { matches: [...] } | null. Null means "not a block+section", which
// is the signal to fall through to geocoding — a town name must not become a
// survey lookup.
//
// Runs BEFORE Mapbox because "B-24 S-9" is not a place name and geocoding it
// returns confident nonsense.
async function searchGloBlockSection(query, { signal, proximity } = {}) {
  const parsed = parseBlockSection(query);
  if (!parsed) return null;

  const params = new URLSearchParams({ block: parsed.block, section: parsed.section });
  if (proximity) {
    params.set("lng", String(proximity.lng));
    params.set("lat", String(proximity.lat));
  }

  const res = await fetchWithAuth(`/api/v1/glo/ols/search?${params.toString()}`, { signal });
  if (!res.ok) return { matches: [], parsed };
  const data = await res.json();
  return { matches: Array.isArray(data.matches) ? data.matches : [], parsed, truncated: !!data.truncated };
}

/* ------------------------------------------------------------------ */
/* HOOK                                                               */
/* ------------------------------------------------------------------ */

/**
 * Programmatic camera control + place-name search.
 *
 *   const { flyToCoords, flyToPlace, search } = useFlyTo({ map });
 *
 *   flyToCoords(lat, lng, { zoom: 14 })
 *   await flyToPlace("Midland TX")
 *       -> { ok: true, name, lat, lng } | { ok: false, reason }
 *
 *   // search() is the same as flyToPlace but without the camera move,
 *   // useful if you want to show a confirmation UI first.
 */
/** A one-line label for a match, used as the flown-to name. */
export function gloMatchName(m) {
  const bits = [`Block ${m.block}`, `Section ${m.section}`];
  if (m.survey_name) bits.push(m.survey_name);
  if (m.county) bits.push(`${m.county} Co.`);
  return bits.join(" · ");
}

export function useFlyTo({ map }) {
  const flyToCoords = useCallback(
    (lat, lng, opts = {}) => {
      if (!map) return;
      if (Number.isNaN(lat) || Number.isNaN(lng)) return;
      map.flyTo({
        center: [lng, lat],
        zoom: opts.zoom ?? 14,
        duration: opts.duration ?? 1200,
        essential: true,
      });
    },
    [map]
  );

  const flyToBounds = useCallback(
    (bbox, opts = {}) => {
      if (!map || !Array.isArray(bbox) || bbox.length !== 4) return;
      map.fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        {
          padding: opts.padding ?? 60,
          maxZoom: opts.maxZoom ?? 14,
          duration: opts.duration ?? 1000,
        }
      );
    },
    [map]
  );

  const search = useCallback(
    async (query, { signal } = {}) => {
      const q = (query ?? "").trim();
      if (!q) return { ok: false, reason: "empty" };

      // 1. Raw lat/lng paste — short-circuit, no network
      const parsed = tryParseLatLng(q);
      if (parsed) {
        return { ok: true, ...parsed, name: `${parsed.lat}, ${parsed.lng}`, source: "coords" };
      }

      // 2. GLO survey block + section.
      //
      // A SINGLE MATCH IS AN ANSWER; SEVERAL ARE A QUESTION. 65% of
      // block+section pairs are unique and fly straight through. The rest are
      // genuinely ambiguous — 58% of multi-match pairs name places more than
      // 50km apart — so this returns them and moves no camera. Choosing the
      // nearest silently would be a coin flip wearing an answer.
      try {
        const glo = await searchGloBlockSection(q, { signal, proximity: map ? map.getCenter() : undefined });
        if (glo) {
          if (glo.matches.length === 1) {
            const m = glo.matches[0];
            return {
              ok: true, source: "glo", lat: m.lat, lng: m.lng, bbox: m.bbox,
              name: gloMatchName(m), match: m,
            };
          }
          if (glo.matches.length > 1) {
            return {
              ok: true, source: "glo", ambiguous: true,
              matches: glo.matches, truncated: glo.truncated,
              name: `Block ${glo.parsed.block}, Section ${glo.parsed.section}`,
            };
          }
          // Parsed as a block+section and matched nothing. Do NOT fall through
          // to geocoding: "B-24 S-9" is not a place name, and Mapbox would
          // answer it with something confident and wrong.
          return { ok: false, reason: "not_found" };
        }
      } catch (err) {
        if (err?.name === "AbortError") return { ok: false, reason: "aborted" };
        console.error("[useFlyTo] GLO search error", err);
        // Fall through to geocoding rather than failing the whole search.
      }

      // 3. Mapbox geocoding
      try {
        const proximity = map ? map.getCenter() : undefined;
        const hit = await geocodeMapbox(q, { signal, proximity });
        if (!hit) return { ok: false, reason: "not_found" };
        return { ok: true, ...hit, source: "mapbox" };
      } catch (err) {
        if (err?.name === "AbortError") return { ok: false, reason: "aborted" };
        console.error("[useFlyTo] geocode error", err);
        return { ok: false, reason: "error" };
      }
    },
    [map]
  );

  const flyToPlace = useCallback(
    async (query, opts = {}) => {
      const r = await search(query, { signal: opts.signal });
      if (!r.ok) return r;

      // Ambiguous: the caller owns the choice. Returning without moving is the
      // point — a camera move here would pick for them.
      if (r.ambiguous) return r;

      if (r.bbox) {
        flyToBounds(r.bbox, opts);
      } else {
        flyToCoords(r.lat, r.lng, opts);
      }
      return r;
    },
    [search, flyToCoords, flyToBounds]
  );

  return { flyToCoords, flyToBounds, flyToPlace, search };
}
